<template>
    <div v-if="!loading">
        <h3 class="title typographies-heading-h3">
            {{ sectionTitle }}
        </h3>
        <h4 v-if="showLimits" class="subtitle typographies-heading-h4">
            {{ $l10n.__('Crédits') }}
        </h4>
        <div class="subscription-manager__buy-quota">
            <p class="typographies-paragraph-medium-regular">
                {{
                    $l10n.__(
                        'Votre abonnement inclus des crédits liés au plan choisi. Si vous ne pouvez plus effectuer certaines actions car vos crédits sont épuisés, il est possible d’en acheter des nouveaux en fonction de vos besoins.',
                    )
                }}
            </p>
            <CompassButton
                v-if="!licenseAssignement && shouldDisplayBuyQuotaBtn"
                :label="$t.__('common.actions.buy_credits')"
                class="buy-quota-btn"
                @click="onClickBuyQuota"
            />
        </div>
        <LimitsAndCreditsTable :items="listQuota" />

        <div v-if="showLimits" class="limits-section">
            <h4 class="subtitle typographies-heading-h4">
                {{ $l10n.__('Limites') }}
            </h4>
            <div class="subscription-manager__buy-quota">
                <p class="typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Votre abonnement inclus des limites liées au plan choisi. Si vous ne pouvez plus effectuer certaines actions car une ou plusieurs limites sont atteintes, il est possible de passer au plan supérieur.',
                        )
                    }}
                </p>
                <CompassButton
                    :label="$t.__('common.actions.contact_sales')"
                    class="buy-quota-btn"
                    @click="openIntercom"
                />
            </div>
            <LimitsAndCreditsTable :items="limitsList" type="limit" />
        </div>
        <ChargebeeQuotaDialog
            v-if="shouldDisplayDialogChargebeeQuota"
            :credits="listQuota"
            @onClose="shouldDisplayDialogChargebeeQuota = false"
            @onValidate="onValidateQuotaBuying"
        />
    </div>
</template>
<script>
import ChargebeeQuotaDialog from 'vcomponents/sellsyaccountmanager/SubscriptionManager/quota/ChargebeeQuotaDialog.vue';
import { goToUrl } from 'js/vue/helpers.js';
import Env from 'Services/Env.js';
import subscription from 'api/sellsyaccountmanager/subscription.js';
import QuotaDictionary from 'Dictionaries/quota/QuotaDictionary.js';
import { createNamespacedHelpers } from 'vuex';
import { cloneDeep } from 'lodash';
import LimitsAndCreditsTable from 'vcomponents/sellsyaccountmanager/SubscriptionManager/quota/LimitsAndCreditsTable.vue';

const { mapState: mapCorpState } = createNamespacedHelpers('corp');
const { mapGetters: mapSubscriptionGetter } = createNamespacedHelpers('subscriptionManager');

export default {
    components: {
        ChargebeeQuotaDialog,
        LimitsAndCreditsTable,
    },
    props: {
        isChargebeeProvider: {
            type: Boolean,
            default: false,
        },
        chargebeeThirdPartyPayerId: {
            type: String,
            default: '',
        },
        licenseAssignement: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            shouldDisplayBuyQuotaBtn: true,
            shouldDisplayDialogChargebeeQuota: false,
            creditsLabel: [],
            listQuota: [],
            limitsList: [],
            loading: true,
        };
    },
    computed: {
        ...mapCorpState(['pricingVersion']),
        ...mapSubscriptionGetter(['activePlugins']),
        sectionTitle() {
            return this.showLimits
                ? this.$l10n.__('Mes crédits et limites')
                : this.$l10n.__('Mes crédits');
        },
        showLimits() {
            return this.pricingVersion >= 5 && !this.limitsList.some((item) => item.limit === null);
        },
    },
    async mounted() {
        if (this.isChargebeeProvider) {
            this.shouldDisplayBuyQuotaBtn = !this.chargebeeThirdPartyPayerId;
        }
        this.initQuotaDictionary();
        await this.getQuotasAndCredits();
    },
    methods: {
        initQuotaDictionary() {
            if (this.pricingVersion >= 5) {
                this.listQuota = cloneDeep(QuotaDictionary.v5.default);
                this.limitsList = cloneDeep(QuotaDictionary.v5.limits);
                if (
                    this.activePlugins.some((plugin) => {
                        return plugin && plugin.id === 'marketing';
                    })
                ) {
                    const marketingArray = cloneDeep(QuotaDictionary.v5.marketing);
                    this.listQuota.push(...marketingArray);
                }
            } else {
                this.listQuota = cloneDeep(QuotaDictionary.default);
            }
        },
        mapItemsToList(list, key, value) {
            return list.map((item) => {
                if (item.id === key) {
                    item.limit = value.limit;
                    item.used = value.used;
                }
                return item;
            });
        },
        getQuotasAndCredits() {
            return subscription.getQuotasAndCredits().then(({ data: quotasAndCredit }) => {
                for (const [key, value] of Object.entries(quotasAndCredit)) {
                    this.listQuota = this.mapItemsToList(this.listQuota, key, value);
                    this.limitsList = this.mapItemsToList(this.limitsList, key, value);
                }
                this.loading = false;
            });
        },
        onClickBuyQuota() {
            //we dont have to check featureFlag cause we already check it on computed in parent
            if (this.isChargebeeProvider) {
                this.shouldDisplayDialogChargebeeQuota = true;
            } else {
                goToUrl('/?_f=planbuilder&action=quota');
            }
        },
        async onValidateQuotaBuying(data) {
            await window.ChargebeeGlobal.initChargebeeScript();
            let chargebeeInstance;
            try {
                chargebeeInstance = window.Chargebee.getInstance();
                chargebeeInstance.closeAll();
            } catch (error) {
                chargebeeInstance = window.Chargebee.init({
                    site: Env.get('chargebee').site ?? '',
                });
            }
            this.shouldDisplayDialogChargebeeQuota = false;
            setTimeout(async () => {
                await chargebeeInstance.openCheckout({
                    hostedPage: () => {
                        return axios
                            .post('/rest/chargebee/charges-checkout', data)
                            .then((response) => response.data)
                            .catch(() => {
                                this.$pushToast({
                                    type: 'error',
                                    icon: true,
                                    text: this.$l10n.__(
                                        'Une erreur est survenue, merci de réessayer ultérieurement',
                                    ),
                                    close: true,
                                });
                                chargebeeInstance.closeAll();
                            });
                    },
                    success: () => {
                        chargebeeInstance.closeAll();
                        this.$pushToast({
                            type: 'success',
                            icon: true,
                            text: this.$l10n.__(
                                'Recharge validée, vos crédits vous seront ajoutés à réception du paiement',
                            ),
                            close: true,
                        });
                    },
                    close: () => window.ChargebeeGlobal.clearChargebeeScript(),
                });
            }, 200);
        },
        openIntercom() {
            window.Intercom('show');
        },
    },
};
</script>

<style lang="scss" scoped>
p {
    margin: 0;
}

.progress-list {
    margin-top: var(--spacing-3);
}

.title {
    margin-bottom: var(--spacing-1);
    margin-top: var(--spacing-6);
}

.subtitle {
    margin: 0 0 var(--spacing-1);
}

.limits-section {
    margin: var(--spacing-4) 0 0;
}

.subscription-manager__buy-quota {
    align-items: flex-start;
    display: flex;
    gap: var(--spacing-5);
    justify-content: space-between;

    .buy-quota-btn {
        flex-shrink: 0;
    }
}
</style>
